import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { useCompany } from '../hooks/useApi';
import { toast } from 'sonner';
import { Building, Upload, X, Loader2, Save, Plus } from 'lucide-react';

// Standard payment conditions
const STANDARD_PAYMENT_CONDITIONS = [
  'Due on receipt',
  'Net 7',
  'Net 15',
  'Net 30',
  'Net 45',
  'Net 60',
];

const SettingsPage = () => {
  const { getCompany, updateCompany, loading } = useCompany();
  const fileInputRef = useRef(null);
  
  const [company, setCompany] = useState({
    name: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    tax_id: '',
    email: '',
    phone: '',
    logo_base64: null,
    default_currency: 'USD',
    default_payment_conditions: []
  });
  const [saving, setSaving] = useState(false);
  const [newCondition, setNewCondition] = useState('');

  useEffect(() => {
    loadCompany();
  }, []);

  const loadCompany = async () => {
    const { data } = await getCompany();
    if (data) setCompany(data);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await updateCompany(company);
      if (!error) {
        toast.success('Settings saved');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 500000) {
      toast.error('Image must be smaller than 500KB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setCompany(c => ({ ...c, logo_base64: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setCompany(c => ({ ...c, logo_base64: null }));
  };

  const addCondition = () => {
    if (!newCondition.trim()) return;
    if (company.default_payment_conditions.includes(newCondition.trim())) {
      toast.error('Condition already exists');
      return;
    }
    setCompany(c => ({
      ...c,
      default_payment_conditions: [...c.default_payment_conditions, newCondition.trim()]
    }));
    setNewCondition('');
  };

  const removeCondition = (index) => {
    setCompany(c => ({
      ...c,
      default_payment_conditions: c.default_payment_conditions.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Company Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your business profile</p>
        </div>
        <Button 
          data-testid="save-settings-btn"
          onClick={handleSave}
          disabled={saving}
          className="gap-2 bg-accent hover:bg-accent/90"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Company Logo */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building className="w-5 h-5" />
            Company Logo
          </CardTitle>
          <CardDescription>
            Upload your company logo to display on invoices
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            {company.logo_base64 ? (
              <div className="relative">
                <img 
                  src={company.logo_base64} 
                  alt="Company Logo"
                  className="w-32 h-32 object-contain border rounded-lg bg-white"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute -top-2 -right-2 w-6 h-6"
                  onClick={removeLogo}
                  data-testid="remove-logo-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center text-muted-foreground bg-slate-50">
                <Building className="w-8 h-8" />
              </div>
            )}
            <div>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleLogoUpload}
              />
              <Button 
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="gap-2"
                data-testid="upload-logo-btn"
              >
                <Upload className="w-4 h-4" />
                Upload Logo
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                PNG, JPG up to 500KB
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Business Details</CardTitle>
          <CardDescription>
            This information will appear on your invoices
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Company Name</Label>
            <Input
              data-testid="company-name-input"
              value={company.name}
              onChange={(e) => setCompany(c => ({ ...c, name: e.target.value }))}
              placeholder="Your Company Name"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Email</Label>
              <Input
                data-testid="company-email-input"
                type="email"
                value={company.email}
                onChange={(e) => setCompany(c => ({ ...c, email: e.target.value }))}
                placeholder="billing@company.com"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                data-testid="company-phone-input"
                value={company.phone}
                onChange={(e) => setCompany(c => ({ ...c, phone: e.target.value }))}
                placeholder="+1 234 567 890"
              />
            </div>
          </div>
          <div>
            <Label>Address</Label>
            <Input
              data-testid="company-address-input"
              value={company.address}
              onChange={(e) => setCompany(c => ({ ...c, address: e.target.value }))}
              placeholder="Street address"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>City</Label>
              <Input
                data-testid="company-city-input"
                value={company.city}
                onChange={(e) => setCompany(c => ({ ...c, city: e.target.value }))}
                placeholder="City"
              />
            </div>
            <div>
              <Label>Postal Code</Label>
              <Input
                data-testid="company-postal-input"
                value={company.postal_code}
                onChange={(e) => setCompany(c => ({ ...c, postal_code: e.target.value }))}
                placeholder="12345"
              />
            </div>
            <div>
              <Label>Country</Label>
              <Input
                data-testid="company-country-input"
                value={company.country}
                onChange={(e) => setCompany(c => ({ ...c, country: e.target.value }))}
                placeholder="Country"
              />
            </div>
          </div>
          <div>
            <Label>Tax ID / VAT Number</Label>
            <Input
              data-testid="company-taxid-input"
              value={company.tax_id}
              onChange={(e) => setCompany(c => ({ ...c, tax_id: e.target.value }))}
              placeholder="Your tax identification number"
            />
          </div>
        </CardContent>
      </Card>

      {/* Invoice Defaults */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Invoice Defaults</CardTitle>
          <CardDescription>
            Default settings for new invoices
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Default Currency</Label>
            <Select 
              value={company.default_currency}
              onValueChange={(v) => setCompany(c => ({ ...c, default_currency: v }))}
            >
              <SelectTrigger data-testid="default-currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD ($)</SelectItem>
                <SelectItem value="CZK">CZK (Kč)</SelectItem>
                <SelectItem value="EUR">EUR (€)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Separator />
          
          <div>
            <Label>Default Payment Conditions</Label>
            <p className="text-sm text-muted-foreground mb-3">
              Select standard terms or add custom ones. These will auto-calculate due dates.
            </p>
            
            {/* Quick add standard conditions */}
            <div className="flex flex-wrap gap-2 mb-4">
              {STANDARD_PAYMENT_CONDITIONS.map((cond) => {
                const isAdded = company.default_payment_conditions.includes(cond);
                return (
                  <Button
                    key={cond}
                    variant={isAdded ? "secondary" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (isAdded) {
                        setCompany(c => ({
                          ...c,
                          default_payment_conditions: c.default_payment_conditions.filter(pc => pc !== cond)
                        }));
                      } else {
                        setCompany(c => ({
                          ...c,
                          default_payment_conditions: [...c.default_payment_conditions, cond]
                        }));
                      }
                    }}
                    data-testid={`toggle-condition-${cond.replace(/\s/g, '-')}`}
                  >
                    {isAdded ? '✓ ' : ''}{cond}
                  </Button>
                );
              })}
            </div>
            
            {/* Custom condition input */}
            <div className="flex gap-2 mb-3">
              <Input
                data-testid="new-condition-input"
                value={newCondition}
                onChange={(e) => setNewCondition(e.target.value)}
                placeholder="Add custom term (e.g., Net 90)"
                onKeyDown={(e) => e.key === 'Enter' && addCondition()}
              />
              <Button 
                variant="outline"
                onClick={addCondition}
                data-testid="add-condition-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Show custom (non-standard) conditions */}
            {company.default_payment_conditions.filter(c => !STANDARD_PAYMENT_CONDITIONS.includes(c)).length > 0 && (
              <div className="mt-3">
                <Label className="text-xs text-muted-foreground">Custom conditions:</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {company.default_payment_conditions
                    .filter(cond => !STANDARD_PAYMENT_CONDITIONS.includes(cond))
                    .map((cond, index) => (
                      <div 
                        key={index}
                        className="flex items-center gap-1 bg-secondary px-3 py-1 rounded-full text-sm"
                      >
                        <span>{cond}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-5 h-5 ml-1"
                          onClick={() => removeCondition(company.default_payment_conditions.indexOf(cond))}
                          data-testid={`remove-condition-${index}`}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
